import { defineStore } from 'pinia'

import { useRightPanelStore } from '@/stores/rightPanel'
import { hashPlainText, type FlashLockFingerprint } from '@/utils/flashLock'

const STORAGE_PREFIX = 'escribonauta.concentration.v1.'

export interface ConcentrationChapterBaseline {
  lastWordCount: number
}

export interface ConcentrationSummary {
  startedAt: number
  endedAt: number
  addedWords: number
}

interface PersistedConcentration {
  active: boolean
  startedAt: number | null
  addedWords: number
  showCongratulations: boolean
  flashEnabled: boolean
  chapters: Record<string, ConcentrationChapterBaseline>
  locks: Record<string, FlashLockFingerprint>
  lastSummary: ConcentrationSummary | null
}

let pageIsUnloading = false
let persistTimer: ReturnType<typeof setTimeout> | undefined

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`
}

function readPersisted(projectId: string): PersistedConcentration | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(storageKey(projectId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedConcentration
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

function writePersisted(projectId: string, data: PersistedConcentration) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(data))
  } catch {
    // Ignore quota or private-mode failures. The in-memory session still runs.
  }
}

function normalizeLock(value: unknown): FlashLockFingerprint | null {
  if (typeof value === 'string') {
    return { length: value.length, hash: hashPlainText(value) }
  }
  if (!value || typeof value !== 'object') return null
  const length = (value as { length?: unknown }).length
  const hash = (value as { hash?: unknown }).hash
  if (typeof length !== 'number' || !Number.isFinite(length) || length < 0) return null
  if (typeof hash !== 'number' || !Number.isFinite(hash)) return null
  return { length, hash: hash >>> 0 }
}

function normalizeLocks(raw: unknown): {
  locks: Record<string, FlashLockFingerprint>
  migrated: boolean
} {
  if (!raw || typeof raw !== 'object') return { locks: {}, migrated: false }
  const locks: Record<string, FlashLockFingerprint> = {}
  let migrated = false
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') migrated = true
    const lock = normalizeLock(value)
    if (lock) locks[id] = lock
  }
  return { locks, migrated }
}

function emptyPersisted(): PersistedConcentration {
  return {
    active: false,
    startedAt: null,
    addedWords: 0,
    showCongratulations: false,
    flashEnabled: false,
    chapters: {},
    locks: {},
    lastSummary: null,
  }
}

export const useConcentrationModeStore = defineStore('concentrationMode', {
  state: () => ({
    projectId: null as string | null,
    active: false,
    startedAt: null as number | null,
    addedWords: 0,
    showCongratulations: false,
    flashEnabled: false,
    chapters: {} as Record<string, ConcentrationChapterBaseline>,
    locks: {} as Record<string, FlashLockFingerprint>,
    mobileNavigationLock: false,
    lastSummary: null as ConcentrationSummary | null,
  }),

  actions: {
    bindProject(projectId: string) {
      if (!projectId || this.projectId === projectId) return
      if (this.projectId) {
        this.flushPersist()
      }
      this.projectId = projectId
      const saved = readPersisted(projectId) ?? emptyPersisted()
      this.active = saved.active
      this.startedAt = saved.active ? saved.startedAt : null
      this.addedWords = saved.active ? saved.addedWords : 0
      this.showCongratulations = saved.showCongratulations
      this.flashEnabled = saved.active ? !!saved.flashEnabled : false
      this.chapters = saved.active ? { ...saved.chapters } : {}
      const normalizedLocks = normalizeLocks(saved.locks)
      this.locks = saved.active ? normalizedLocks.locks : {}
      this.lastSummary = saved.lastSummary
      if (saved.active && normalizedLocks.migrated) {
        this.flushPersist()
      }
    },

    enter() {
      if (!this.projectId || this.active) return
      this.active = true
      this.startedAt = Date.now()
      this.addedWords = 0
      this.chapters = {}
      this.locks = {}
      this.flashEnabled = false
      this.showCongratulations = false
      this.flushPersist()
    },

    exit(options?: { openResults?: boolean }) {
      if (!this.active || !this.startedAt) {
        this.active = false
        return
      }
      const openResults = options?.openResults !== false
      this.lastSummary = {
        startedAt: this.startedAt,
        endedAt: Date.now(),
        addedWords: this.addedWords,
      }
      this.active = false
      this.startedAt = null
      this.addedWords = 0
      this.chapters = {}
      this.locks = {}
      this.mobileNavigationLock = false
      this.flashEnabled = false
      this.showCongratulations = true
      this.flushPersist()
      if (openResults && this.projectId) {
        const rightPanelStore = useRightPanelStore()
        rightPanelStore.selectDetail('concentration')
        rightPanelStore.showPanel()
      }
    },

    toggle() {
      if (this.active) {
        this.exit()
        return
      }
      this.enter()
    },

    observeChapterWords(chapterId: string, wordCount: number) {
      if (!this.active || !chapterId || !Number.isFinite(wordCount)) return
      const previous = this.chapters[chapterId]
      if (!previous) {
        this.chapters[chapterId] = { lastWordCount: wordCount }
        this.schedulePersist()
        return
      }
      if (wordCount > previous.lastWordCount) {
        this.addedWords += wordCount - previous.lastWordCount
      }
      previous.lastWordCount = wordCount
      this.schedulePersist()
    },

    setFlashEnabled(enabled: boolean) {
      if (!this.active) return
      this.flashEnabled = enabled
      if (!enabled) {
        this.locks = {}
      }
      this.flushPersist()
    },

    setChapterLock(chapterId: string, lock: FlashLockFingerprint) {
      if (!this.active || !this.flashEnabled || !chapterId) return
      const current = this.locks[chapterId]
      if (current && current.length === lock.length && current.hash === lock.hash) return
      this.locks[chapterId] = { length: lock.length, hash: lock.hash }
      this.schedulePersist()
    },

    setMobileNavigationLock(locked: boolean) {
      this.mobileNavigationLock = locked
    },

    dismissCongratulations() {
      if (!this.showCongratulations) return
      this.showCongratulations = false
      this.flushPersist()
    },

    markPageUnloading() {
      pageIsUnloading = true
    },

    clearPageUnloading() {
      pageIsUnloading = false
    },

    releaseIfLeavingEditor() {
      if (pageIsUnloading) return
      this.exit({ openResults: false })
    },

    schedulePersist() {
      if (persistTimer) clearTimeout(persistTimer)
      persistTimer = setTimeout(() => {
        persistTimer = undefined
        this.flushPersist()
      }, 400)
    },

    flushPersist() {
      if (persistTimer) {
        clearTimeout(persistTimer)
        persistTimer = undefined
      }
      if (!this.projectId) return
      const saved = readPersisted(this.projectId) ?? emptyPersisted()
      writePersisted(this.projectId, {
        active: this.active,
        startedAt: this.startedAt,
        addedWords: this.addedWords,
        showCongratulations: this.showCongratulations,
        flashEnabled: this.flashEnabled,
        chapters: { ...this.chapters },
        locks: { ...this.locks },
        lastSummary: this.lastSummary ?? saved.lastSummary,
      })
    },
  },
})
